import { UseEntityIssuesResult } from 'hooks/useEntityIssues';
import InconsistenciesCard, { IssueObjectType } from 'app/extreport/concierge/InconsistenciesCard';
import ClarificationsCard from 'app/content-workflow/element-editor/ClarificationsCard';
import React from 'react';
import { Alert } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import useEverTrue from 'hooks/useEverTrue';

export interface InconsistenciesWrapperProps {
    issues: UseEntityIssuesResult;
    onOpenConcierge: (questionId?: string) => void;
    showInconsistencies?: boolean;
    showClarifications?: boolean;
    inconsistenciesExpandToken?: number;
    clarificationsExpandToken?: number;
    objectType?: IssueObjectType;
}

export default function InconsistenciesWrapper({
    issues,
    onOpenConcierge,
    showInconsistencies = true,
    showClarifications = true,
    inconsistenciesExpandToken,
    clarificationsExpandToken,
    objectType = 'report',
}: InconsistenciesWrapperProps) {
    const { t } = useBundleTranslation('app/extreport/concierge');
    const { context, skipQuestion, skipAllQuestions, undoQuestion } = issues;

    // Keep the inconsistencies card rendered (collapsed to `Resolved`) after a refetch clears its
    // rows in-session — resolution happens in the Measure Conflicts editor, not here (spec §4.3/§4.4)
    const everHadInconsistencies = useEverTrue((context?.inconsistencies.length ?? 0) > 0);
    const renderInconsistencies = showInconsistencies && everHadInconsistencies;

    if (!context) return null;

    const { inconsistencies, clarifyingQuestions } = context;
    const inconsistenciesClear = inconsistencies.length === 0;
    const questionsClear = clarifyingQuestions.every((q) => q.status !== 'Unanswered');
    const hasAnyItems = renderInconsistencies || (showClarifications && clarifyingQuestions.length > 0);
    const allClear = hasAnyItems && inconsistenciesClear && questionsClear;

    return (
        <>
            {allClear && (
                <Alert icon={<IconMi icon={'check'} />} severity={'success'} variant={'standard'}>
                    {t('all_issues_resolved')}
                </Alert>
            )}
            {renderInconsistencies && (
                <InconsistenciesCard
                    inconsistencies={inconsistencies}
                    compact
                    objectType={objectType}
                    canResolve={context.isKgManager === true}
                    forceExpandToken={inconsistenciesExpandToken}
                />
            )}
            {showClarifications && clarifyingQuestions.length > 0 && (
                <ClarificationsCard
                    questions={clarifyingQuestions}
                    onSkip={skipQuestion}
                    onUndo={undoQuestion}
                    onSkipAll={skipAllQuestions}
                    onOpenConcierge={onOpenConcierge}
                    objectType={objectType}
                    forceExpandToken={clarificationsExpandToken}
                />
            )}
        </>
    );
}
